import * as _ from 'lodash';
import qs from 'qs';
import { useState } from 'react';
import type { InjectedRouter } from 'react-router';

import { errorMessageFromResponse } from '../../../helpers/errors';
import { PasswordStrengthLabel, testPasswordStrength } from '../../../helpers/passwordStrength';
import useInputValue from '../../../hooks/useInputValue';
import { post } from '../../../redux/api';
import type { PasswordResetRequest, PasswordResetResponse } from '../../../../typings/api';
import type { FailedFetchResponse } from '../../../helpers/errors';
import type { PasswordStrength } from '../../../helpers/passwordStrength';
import type { RouterProps } from '../../../types';

import useFocusInputs from './useFocusInputs';
import type { AuthInput } from './AuthContext';

type UsePasswordResetProps = {
  location: RouterProps['location'];
  router: InjectedRouter;
};

type UsePasswordReset = {
  email: AuthInput;
  password: AuthInput;
  hasSentResetEmail: boolean;
  isRequestPending: boolean;
  isSubmitDisabled: boolean;
  passwordStrength: PasswordStrength | null;
  onSubmit: () => Promise<void>;
  alert: PasswordResetAlert | null;
  showResendText: boolean;
};

type PasswordResetAlert = {
  message: string;
  type: 'success' | 'info' | 'error';
};

//  The first endpoint is to request the token be sent via email,
//  the 2nd actually resets the user's password.
const ENDPOINT = {
  REQUEST: '/api/auth/request-password-reset',
  RESET: '/api/auth/password-reset',
};

//  Redirect to login 2s after successfully changing password, carrying the reset email (and any `next`
//  deep-link) so the login form starts prefilled instead of blank.
function redirectToLogin(router: InjectedRouter, email: string, next?: string): void {
  const REDIRECT_TIMEOUT = 1000 * 2;
  const query = qs.stringify({ email: email || undefined, next }, { skipNulls: true });
  _.delay(() => router.push(query ? `/auth/login?${query}` : '/auth/login'), REDIRECT_TIMEOUT);
}

/**
 * Hook for resetting password.
 *
 * Resetting the password is a two-step process. First the user enters their email and is
 * sent a password reset token. Clicking the link will send them back to this page
 * with both a token + email as query params, which are then used for the next step.
 */
const usePasswordReset = ({ location, router }: UsePasswordResetProps): UsePasswordReset => {
  const { query } = location;
  const [email, onEmailChange] = useInputValue(query.email || '');
  const [password, onPasswordChange] = useInputValue('');
  const [alert, setAlert] = useState<PasswordResetAlert | null>(null);
  const [isRequestPending, setIsRequestPending] = useState<boolean>(false);
  //  After successfully sending any email, we change the submit button text to say "Resend".
  const [showResendText, setShowResendText] = useState<boolean>(false);
  const { emailInputRef, passwordInputRef } = useFocusInputs();

  //  This tells us we're on "step 2" of the password reset process: the user arrived via the emailed
  //  link, whose server-side redirect stashed the token in the session and stamped `reset=1` exactly. A
  //  bare `?email=` (the login flow's forgot-password link) only prefills the address for step 1.
  const hasSentResetEmail = !!(query.email && query.reset === '1');

  const passwordStrength: PasswordStrength | null = hasSentResetEmail
    ? testPasswordStrength(password)
    : null;

  const isSubmitDisabled = hasSentResetEmail
    ? password.length === 0 && passwordStrength?.label !== PasswordStrengthLabel.Excellent
    : email.length === 0;

  const onSubmit = async () => {
    //  Reset any alerts on submit.
    setAlert(null);
    setIsRequestPending(true);
    try {
      const endpoint = hasSentResetEmail
        ? ENDPOINT.RESET
        : ENDPOINT.REQUEST;

      const { data } = await post<PasswordResetRequest, PasswordResetResponse>(endpoint, {
        email,
        password: password || undefined,
        //  On the request step, carry the `?next=` deep-link so the server can thread it through the
        //  emailed reset link (opened in a fresh browser context that has no other state).
        ...(hasSentResetEmail ? {} : { next: query.next || undefined }),
      });

      //  The response envelope's `data` is typed optional; the endpoint always answers with a message.
      const message = data?.message ?? '';
      if (hasSentResetEmail) {
        setAlert({
          message,
          type: 'success',
        });
        redirectToLogin(router, email, query.next);
      } else {
        setAlert({
          message,
          type: 'info',
        });
      }
      setShowResendText(true);
      setIsRequestPending(false);
    } catch (error) {
      setAlert({
        type: 'error',
        message: errorMessageFromResponse(error as FailedFetchResponse),
      });
      setIsRequestPending(false);
    }
  };

  return {
    email: {
      ref: emailInputRef,
      value: email,
      onChange: onEmailChange,
    },
    password: {
      ref: passwordInputRef,
      value: password,
      onChange: onPasswordChange,
    },
    hasSentResetEmail,
    isRequestPending,
    isSubmitDisabled,
    passwordStrength,
    onSubmit,
    alert,
    showResendText,
  };
};

export default usePasswordReset;
